import { createContext, useContext, ReactNode } from 'react';
import { updateBaseUrls } from '../services/api/apiCall';

// Define the shape of our configuration
interface AppConfig {
  environment: string;
  rbLoginUrl: string;
  mntBaseUrl: string;
  bookingBaseUrl: string;
  subBaseUrl: string;
}

// Configuration for each environment
const configurations = {
  local: {
    environment: 'local',
    rbLoginUrl: 'https://mittkontost.riksbyggen.se',
    mntBaseUrl: 'https://localhost:7049',
    bookingBaseUrl: 'https://rb-sln-booking-api-st.azurewebsites.net',
    subBaseUrl: 'https://rb-sln-sub-api-st.azurewebsites.net'
  },
  dev: {
    environment: 'Dev',
    rbLoginUrl: 'https://mittkontost.riksbyggen.se',
    mntBaseUrl: 'https://solution-exogo-backend-dev.calmmushroom-70746862.westeurope.azurecontainerapps.io',
    bookingBaseUrl: 'https://rb-sln-booking-api-st.azurewebsites.net',
    subBaseUrl: 'https://rb-sln-sub-api-st.azurewebsites.net'
  },
  st: {
    environment: 'ST',
    rbLoginUrl: 'https://mittkontost.riksbyggen.se',
    mntBaseUrl: 'https://solution-exogo-backend-st.lemonwater-27fd49b4.westeurope.azurecontainerapps.io',
    bookingBaseUrl: 'https://rb-sln-booking-api-st.azurewebsites.net',
    subBaseUrl: 'https://rb-sln-sub-api-st.azurewebsites.net'
  },
  at: {
    environment: 'AT',
    rbLoginUrl: 'https://mittkontoat.riksbyggen.se',
    mntBaseUrl: 'https://solution-exogo-backend-at.calmbush-0c3c0d2e.westeurope.azurecontainerapps.io',
    bookingBaseUrl: 'https://apim-exogo-at.azure-api.net',
    subBaseUrl: 'https://rb-sln-sub-api-at.azurewebsites.net'
  },
  prod: {
    environment: 'Prod',
    rbLoginUrl: 'https://mittkonto.riksbyggen.se',
    mntBaseUrl: 'https://solution-exogo-backend.happysmoke-c1d18c2f.westeurope.azurecontainerapps.io',
    bookingBaseUrl: 'https://apim-exogo.azure-api.net',
    subBaseUrl: 'https://rb-sln-sub-api.azurewebsites.net'
  }
};

// Create a context to share the configuration throughout the app
const ConfigContext = createContext<AppConfig>(configurations.dev);

interface ConfigProviderProps {
  children: ReactNode;
}

// Detect which environment we're in based on hostname
const getConfigForHostname = (): AppConfig => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('localhost')) {
    return configurations.local;
  } else if (hostname.includes('digital-underhallsplandev')) {
    return configurations.dev;
  } else if (hostname.includes('digital-underhallsplanst')) {
    return configurations.st;
  } else if (hostname.includes('digital-underhallsplanat')) {
    return configurations.at;
  } else if (hostname.includes('mittkonto.riksbyggen.se') || hostname.includes('digital-underhallsplan.riksbyggen.se')) {
    return configurations.prod;
  }
  
  // Default to dev for safety
  console.warn('Could not detect environment from hostname, using Dev configuration');
  return configurations.dev;
};

export function ConfigProvider({ children }: ConfigProviderProps) {
  // Get the configuration for the current environment
  const config = getConfigForHostname();
  
  // Update the environment variable at runtime
  updateBaseUrls(config);
  
  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

// Custom hook to use the config
export function useConfig() {
  return useContext(ConfigContext);
}